// ERP Assistant — dashboard widget. Read-only. AI is called ONLY when the
// user submits a message via the send button (or Enter key). No autoload
// on mount beyond fetching existing chat history.
import React from "react";
import { api } from "@/lib/api";
import { Card, Btn } from "@/components/UI";
import { useWidgetSize } from "@/components/WidgetReorder";
import { Sparkles, Send, Loader2, RotateCcw, Maximize2, Minimize2, Info, Mic, MicOff } from "lucide-react";

export default function ERPAssistantWidget({ size = "L", onSizeChange }) {
  const ctxSize = useWidgetSize();
  const effSize = (ctxSize && ctxSize !== "FULL") ? ctxSize : size;
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [error, setError] = React.useState("");
  const [recState, setRecState] = React.useState("idle");  // idle | recording | transcribing
  const scrollRef = React.useRef(null);
  const recRef = React.useRef({ mr: null, chunks: [], stream: null });

  // Load recent history (no AI call)
  React.useEffect(() => {
    let alive = true;
    api.get("/assistant/session")
      .then((r) => { if (alive) setMessages(r.messages || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  React.useEffect(() => {
    // Scroll to bottom on new message
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, expanded]);

  const send = async () => {
    const text = (input || "").trim();
    if (!text || busy) return;
    setError("");
    const now = new Date().toISOString();
    setMessages((prev) => [...prev, { role: "user", content: text, at: now }]);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/assistant/chat", { message: text });
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: r.answer,
        at: new Date().toISOString(),
        context_refs: r.context_refs,
      }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "AI hatası";
      setError(msg);
      setMessages((prev) => [...prev, { role: "assistant",
        content: `⚠️ ${msg}`, at: new Date().toISOString() }]);
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("ERP Yardımcı geçmişi silinsin mi?")) return;
    try {
      await api.delete("/assistant/session");
      setMessages([]);
      setError("");
    } catch (e) {
      alert("Sıfırlanamadı: " + (e?.response?.data?.detail || e.message));
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // ─── Voice input via MediaRecorder → Whisper transcript ───
  const startRecording = async () => {
    if (recState !== "idle") return;
    setError("");
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setError("Tarayıcınız ses kaydını desteklemiyor.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferred = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
      const mimeType = preferred.find((m) => MediaRecorder.isTypeSupported?.(m)) || "";
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recRef.current = { mr, chunks: [], stream };
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) recRef.current.chunks.push(e.data); };
      mr.onstop = async () => {
        const { chunks, stream: s } = recRef.current;
        try { s?.getTracks?.().forEach((t) => t.stop()); } catch {}
        setRecState("transcribing");
        const type = mr.mimeType || "audio/webm";
        const blob = new Blob(chunks, { type });
        if (blob.size < 500) {
          setRecState("idle");
          setError("Çok kısa kayıt. Lütfen tekrar deneyin.");
          return;
        }
        try {
          const fd = new FormData();
          const ext = type.includes("mp4") ? "m4a" : (type.includes("webm") ? "webm" : "ogg");
          fd.append("audio", blob, `voice.${ext}`);
          const r = await api.postForm("/assistant/transcribe", fd);
          const text = (r?.text || "").trim();
          if (!text) {
            setError("Ses tanınamadı. Lütfen tekrar deneyin.");
          } else {
            setInput((prev) => (prev ? (prev + " " + text) : text));
          }
        } catch (e) {
          setError("Ses tanıma hatası: " + (e?.response?.data?.detail || e.message));
        } finally {
          setRecState("idle");
        }
      };
      mr.start();
      setRecState("recording");
    } catch (e) {
      setError("Mikrofon erişimi reddedildi. Tarayıcı izinlerini kontrol edin.");
    }
  };

  const stopRecording = () => {
    const { mr } = recRef.current || {};
    if (mr && mr.state !== "inactive") {
      try { mr.stop(); } catch {}
    }
  };

  React.useEffect(() => () => {
    // Cleanup on unmount
    try { recRef.current?.stream?.getTracks?.().forEach((t) => t.stop()); } catch {}
  }, []);

  const visibleMessages = messages.filter((m) => m.role !== "system");
  const bodyHeight = expanded ? "h-[70vh]" : (effSize === "FULL" ? "h-96" : effSize === "L" ? "h-80" : effSize === "M" ? "h-56" : "h-36");
  const compact = effSize === "S";
  const showPlaceholder = !compact && visibleMessages.length === 0;

  return (
    <Card className="p-4 mb-4 bg-gradient-to-br from-indigo-50/60 via-white to-violet-50/50 dark:from-indigo-900/20 dark:via-slate-900 dark:to-violet-900/20 border-indigo-200/60 dark:border-indigo-500/30" data-testid="erp-assistant-widget">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={16} className="text-indigo-600 dark:text-indigo-400"/>
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: "'Manrope', sans-serif" }}>ERP Yardımcı</h3>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-500/40 uppercase tracking-wider font-semibold">Salt Okunur</span>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={reset} className="p-1 text-slate-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded" title="Geçmişi sıfırla" data-testid="assistant-reset-btn">
            <RotateCcw size={12}/>
          </button>
          <button onClick={() => setExpanded(!expanded)} className="p-1 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded" title={expanded ? "Küçült" : "Büyüt"} data-testid="assistant-expand-btn">
            {expanded ? <Minimize2 size={12}/> : <Maximize2 size={12}/>}
          </button>
        </div>
      </div>

      {showPlaceholder && (
        <div className="mb-2 flex items-start gap-2 p-2.5 bg-white/60 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/60 rounded text-xs text-slate-600 dark:text-slate-400" data-testid="assistant-placeholder">
          <Info size={13} className="text-indigo-500 shrink-0 mt-0.5"/>
          <div>
            <b>Örnek soru:</b> "SS10542 neden bekliyor?" — sipariş, üretim, satın alma veya stok bilgisini içeren sorular sorabilirsiniz. Yardımcı yalnız <b>okuma</b> yapar; ERP'de değişiklik oluşturmaz.
          </div>
        </div>
      )}

      <div ref={scrollRef} className={`${bodyHeight} overflow-y-auto space-y-2 pr-1 py-1`} data-testid="assistant-messages">
        {visibleMessages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm leading-snug whitespace-pre-wrap ${
              m.role === "user"
                ? "bg-indigo-600 text-white rounded-tr-sm"
                : "bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-tl-sm"
            }`} data-testid={`assistant-msg-${m.role}-${i}`}>
              {m.content}
              {m.context_refs && m.context_refs.length > 0 && (
                <div className="mt-1.5 text-[10px] opacity-70">Referans: {m.context_refs.join(", ")}</div>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 size={12} className="animate-spin"/> ERP verisi okunuyor…
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-end gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          rows={expanded ? 3 : 1}
          placeholder={recState === "recording" ? "🎙️ Kayıt sürüyor… (mikrofon butonuna tekrar basınca duracak)" : "Bir soru yazın… (Enter ile gönder)"}
          className="flex-1 resize-none text-sm px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/70 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          data-testid="assistant-input"
          disabled={busy || recState === "recording"}
        />
        <button
          type="button"
          onClick={recState === "recording" ? stopRecording : startRecording}
          disabled={busy || recState === "transcribing"}
          className={`h-9 px-2 rounded-lg border transition inline-flex items-center gap-1 ${
            recState === "recording"
              ? "bg-rose-600 border-rose-700 text-white animate-pulse"
              : recState === "transcribing"
                ? "bg-slate-100 border-slate-300 text-slate-500 dark:bg-slate-800 dark:border-slate-600"
                : "bg-white border-slate-200 text-slate-600 hover:border-indigo-400 hover:text-indigo-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
          }`}
          data-testid="assistant-mic-btn"
          title={recState === "recording" ? "Kaydı durdur ve yaz" : (recState === "transcribing" ? "Ses tanınıyor…" : "Sesli soru")}
        >
          {recState === "transcribing"
            ? <Loader2 size={13} className="animate-spin"/>
            : recState === "recording"
              ? <MicOff size={13}/>
              : <Mic size={13}/>}
        </button>
        <Btn variant="primary" onClick={send} disabled={busy || !input.trim() || recState !== "idle"} data-testid="assistant-send-btn">
          {busy ? <Loader2 size={13} className="animate-spin"/> : <Send size={13}/>}
          <span className={compact ? "sr-only" : "hidden sm:inline ml-1"}>Gönder</span>
        </Btn>
      </div>
      {error && <div className="mt-2 text-xs text-rose-600" data-testid="assistant-error">{error}</div>}
    </Card>
  );
}
